import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { reasonLabel } from './smsReasons'

/**
 * Réglages du module Alertes SMS tels que le serveur les connaît (`sms_settings_overview_v1`). Aucun secret : la
 * présence des clés Telnyx n'est connue que par le dernier diagnostic daté (`providerCheck`). Un état non vérifié
 * n'est jamais présenté comme réussi (mandat du 16 octobre 2026, « Activation visible et honnête »).
 */

export const smsModes = ['DISABLED', 'SIMULATION', 'TEST', 'PRODUCTION'] as const
export type SmsMode = (typeof smsModes)[number]

export const providerCheckSchema = z.object({
  checkedAt: z.string(),
  apiKeyConfigured: z.boolean(),
  publicKeyConfigured: z.boolean(),
  apiReachable: z.boolean(),
  profileFound: z.boolean(),
  profileEnabled: z.boolean(),
  webhookUrlMatches: z.boolean(),
  destinations: z.array(z.string()),
  numberAttached: z.boolean(),
  smsTwoWay: z.boolean(),
  numberType: z.string().nullable(),
  dailySpendLimit: z.string().nullable(),
  problems: z.array(z.string()),
})
export type ProviderCheck = z.infer<typeof providerCheckSchema>

const configuredSchema = z.object({
  configured: z.literal(true),
  canManage: z.boolean(),
  mode: z.enum(smsModes),
  senderE164: z.string().nullable(),
  messagingProfileId: z.string().nullable(),
  countries: z.array(z.string()),
  sendingPaused: z.boolean(),
  webhookLastReceivedAt: z.string().nullable(),
  providerCheckedAt: z.string().nullable(),
  // Un diagnostic d'une version antérieure, ou illisible, vaut « non vérifié » plutôt qu'une erreur d'écran.
  providerCheck: z.unknown().transform((value) => {
    const parsed = providerCheckSchema.safeParse(value)
    return parsed.success ? parsed.data : null
  }),
  segmentPriceKnown: z.boolean(),
  updatedAt: z.string(),
  dailyVolumeCap: z.number().int().nullable(),
  outboundToday: z.number().int().nullable(),
  testAllowlist: z.array(z.string()),
})

export const smsSettingsOverviewSchema = z.discriminatedUnion('configured', [
  z.object({ configured: z.literal(false), canManage: z.boolean() }),
  configuredSchema,
])
export type SmsSettingsOverview = z.infer<typeof smsSettingsOverviewSchema>
export type ConfiguredSmsSettings = z.infer<typeof configuredSchema>

export const smsTestResultSchema = z.object({
  state: z.string(),
  reason: z.string().nullable().optional(),
  replayed: z.boolean().optional(),
})
export type SmsTestResult = z.infer<typeof smsTestResultSchema>

/** `+14509907539` → `450-990-7539`. Tout autre format est rendu tel quel. */
export function formatPhone(e164: string | null | undefined): string {
  const digits = /^\+1(\d{10})$/.exec(e164 ?? '')?.[1]
  return digits ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : (e164 ?? '')
}

export interface ServiceState {
  label: string
  tone: StatusTone
}

/** État résumé dans l'en-tête du module. La suspension prime sur tout le reste. */
export function serviceState(overview: SmsSettingsOverview): ServiceState {
  if (!overview.configured) return { label: 'Non configuré', tone: 'neutralLight' }
  if (overview.sendingPaused) return { label: 'Envois suspendus', tone: 'danger' }
  if (overview.mode === 'PRODUCTION') return { label: 'Production autorisée', tone: 'success' }
  if (overview.mode === 'TEST') return { label: 'Pilote autorisé', tone: 'pending' }
  if (!isConfigurationComplete(overview)) return { label: 'Configuration incomplète', tone: 'warning' }
  if (overview.mode === 'SIMULATION') return { label: 'Simulation', tone: 'info' }
  return { label: 'Envois désactivés', tone: 'neutral' }
}

function isConfigurationComplete(settings: ConfiguredSmsSettings): boolean {
  const check = settings.providerCheck
  return Boolean(
    settings.senderE164 &&
    settings.messagingProfileId &&
    check?.apiKeyConfigured &&
    check.publicKeyConfigured,
  )
}

export type StepState = 'done' | 'missing' | 'unverified' | 'active'

export interface ActivationStep {
  key: string
  label: string
  state: StepState
  stateLabel: string
  tone: StatusTone
  note: string
}

function step(key: string, label: string, state: StepState, stateLabel: string, note = ''): ActivationStep {
  const tone: StatusTone =
    state === 'done'
      ? 'success'
      : state === 'missing'
        ? 'warning'
        : state === 'active'
          ? 'danger'
          : 'neutralLight'
  return { key, label, state, stateLabel, tone, note }
}

/**
 * Récapitulatif d'activation : chaque étape se lit sur une donnée réelle (réglages, dernier diagnostic daté,
 * dernier webhook signé reçu, mode). « Configurée » n'implique jamais « vérifiée ».
 */
export function activationSteps(overview: SmsSettingsOverview): ActivationStep[] {
  if (!overview.configured) {
    return [
      step(
        'module',
        'Module disponible',
        'missing',
        'À configurer',
        'Aucun réglage SMS pour l’organisation.',
      ),
    ]
  }
  const check = overview.providerCheck
  const checked = check !== null
  const steps = [
    step('module', 'Module disponible', 'done', 'Disponible'),
    isConfigurationComplete(overview)
      ? step('config', 'Configuration complète', 'done', 'Complète')
      : step(
          'config',
          'Configuration complète',
          checked || !overview.senderE164 || !overview.messagingProfileId ? 'missing' : 'unverified',
          checked || !overview.senderE164 || !overview.messagingProfileId ? 'Incomplète' : 'Non vérifiée',
          !overview.messagingProfileId
            ? 'Identifiant du profil de messagerie à renseigner.'
            : checked
              ? 'Clé API ou clé publique absente des secrets du serveur.'
              : 'Lancer « Vérifier la configuration ».',
        ),
    check?.apiReachable && check.profileFound && check.profileEnabled
      ? step('connection', 'Connexion fournisseur vérifiée', 'done', 'Vérifiée')
      : step(
          'connection',
          'Connexion fournisseur vérifiée',
          checked ? 'missing' : 'unverified',
          checked ? 'En échec' : 'Non vérifiée',
          checked ? (check.problems[0] ?? '') : '',
        ),
    check?.numberAttached && check.smsTwoWay
      ? step('number', 'Numéro associé et SMS activés', 'done', 'Vérifié')
      : step(
          'number',
          'Numéro associé et SMS activés',
          checked && check.apiReachable ? 'missing' : 'unverified',
          checked && check.apiReachable ? 'En échec' : 'Non vérifié',
        ),
    overview.webhookLastReceivedAt
      ? step('webhook', 'Réception webhook vérifiée', 'done', 'Vérifiée')
      : step('webhook', 'Réception webhook vérifiée', 'unverified', 'Jamais reçue'),
    overview.mode === 'TEST' || overview.mode === 'PRODUCTION'
      ? step('pilot', 'Pilote autorisé', 'done', 'Autorisé')
      : step('pilot', 'Pilote autorisé', 'unverified', 'Non autorisé'),
    overview.mode === 'PRODUCTION'
      ? step('production', 'Production autorisée', 'done', 'Autorisée')
      : step(
          'production',
          'Production autorisée',
          'unverified',
          'Non autorisée',
          'Après validation de Francis.',
        ),
  ]
  if (overview.sendingPaused) {
    steps.push(
      step('paused', 'Envois suspendus', 'active', 'Actif', 'Réception, STOP et statuts continuent.'),
    )
  }
  return steps
}

export interface ModeOption {
  mode: Exclude<SmsMode, 'DISABLED'>
  label: string
  description: string
}

export const modeOptions: ModeOption[] = [
  {
    mode: 'SIMULATION',
    label: 'Simulation',
    description: 'Aucun SMS transmis. Statuts simulés pour tester l’interface.',
  },
  { mode: 'TEST', label: 'Pilote', description: 'SMS réels vers les numéros d’équipe autorisés seulement.' },
  {
    mode: 'PRODUCTION',
    label: 'Production',
    description: 'Clients consentants. Activé après validation explicite.',
  },
]

const commandMessages: Record<string, string> = {
  FORBIDDEN: 'Votre rôle ne permet pas de modifier les réglages SMS.',
  SMS_NOT_CONFIGURED: 'Aucun réglage SMS pour l’organisation.',
  SMS_NOT_IN_TEST_ALLOWLIST: 'Ce numéro n’est pas dans la liste des numéros d’équipe autorisés.',
  SMS_MODE_NOT_LIVE: 'Le SMS test exige le mode pilote ou production.',
  ORIGIN_NOT_ALLOWED: 'Cette adresse n’est pas autorisée à appeler le diagnostic.',
  SMS_NOT_FOUND: 'Cet envoi est introuvable.',
  SMS_UNKNOWN_CONTACT: 'Ce numéro est introuvable.',
  SMS_INVALID_NUMBER: 'Numéro invalide : un numéro canadien ou américain à 10 chiffres est attendu.',
  SMS_INVALID_SCOPE: 'Portée inconnue.',
  SMS_WORDING_REQUIRED: 'Le texte accepté et sa version sont obligatoires.',
  SMS_PROOF_REQUIRED: 'L’origine de la preuve est obligatoire.',
  SMS_PROOF_IN_FUTURE: 'La date d’acceptation ne peut pas être dans le futur.',
  SMS_NOTE_REQUIRED: 'Décrivez la demande de retrait reçue.',
  SMS_SCOPE_NOT_ACTIVE: 'Cette portée n’est ni inscrite ni à confirmer : rien à retirer.',
  SMS_NOTHING_TO_WITHDRAW: 'Choisissez au moins une portée ou le blocage de tous les SMS.',
  SMS_BLOCK_NOT_LIFTABLE:
    'Ce blocage ne se lève pas ici : un STOP se lève seulement quand le client répond START.',
  SMS_CONVERSATION_NOT_FOUND: 'Cette conversation est introuvable.',
  SMS_CONVERSATION_VERSION_CONFLICT:
    'La conversation a changé entre-temps. Elle est rechargée : refaites l’action.',
  SMS_INVALID_STATUS: 'Statut inconnu.',
  SMS_REPLY_EMPTY: 'Écrivez une réponse avant d’envoyer.',
  SMS_CLIENT_NOT_FOUND: 'Ce dossier client est introuvable.',
  SMS_TEMPLATE_NOT_FOUND: 'Ce modèle est introuvable.',
  SMS_TEMPLATE_SYSTEM: 'Modèle interne : son texte est tenu par l’application.',
  SMS_TEMPLATE_VERSION_CONFLICT:
    'Une autre version a été enregistrée entre-temps. Le modèle est rechargé : reprenez la modification.',
  SMS_TEMPLATE_EMPTY: 'Le texte ne peut pas être vide.',
  SMS_TEMPLATE_UNCHANGED: 'Texte identique à la version actuelle : aucune nouvelle version.',
  SMS_TEMPLATE_UNKNOWN_VARIABLE: 'Variable non autorisée pour ce modèle.',
  SMS_TEMPLATE_STOP_REQUIRED: 'Mention de désinscription manquante : ajouter « STOP pour vous désinscrire ».',
  SMS_REPLY_UNFILLED_VARIABLE: 'Remplacez les variables du modèle avant d’envoyer.',
  SMS_CAMPAIGN_SEASON_INVALID: 'Choisissez une saison.',
  SMS_CAMPAIGN_ROUTES_REQUIRED: 'Choisissez au moins une route.',
  SMS_CAMPAIGN_ROUTE_INVALID:
    'Une route choisie n’existe plus dans cette saison. Rechargez la liste des routes.',
  SMS_CAMPAIGN_TEMPLATE_INVALID: 'Ce modèle n’est pas (ou plus) actif pour les envois groupés.',
  SMS_CAMPAIGN_DATE_INVALID: 'Date de l’opération : aujourd’hui ou dans les 14 prochains jours.',
  SMS_CAMPAIGN_TIME_REQUIRED: 'Ce modèle annonce une heure : indiquez l’heure estimée.',
  SMS_CAMPAIGN_SCHEDULE_INVALID: 'Heure d’envoi ou d’expiration illisible.',
  SMS_CAMPAIGN_SEND_AT_INVALID: 'Heure d’envoi : maintenant ou dans les 14 prochains jours.',
  SMS_CAMPAIGN_EXPIRY_INVALID: 'Expiration : au moins 15 minutes après l’envoi et au plus 3 jours plus tard.',
  SMS_CAMPAIGN_CONFIRMATION_REQUIRED: 'Cochez la confirmation avant d’envoyer.',
  SMS_CAMPAIGN_TEMPLATE_CHANGED:
    'Le modèle a changé depuis l’aperçu. Vérifiez de nouveau le message avant de confirmer.',
  SMS_CAMPAIGN_AUDIENCE_CHANGED:
    'Les destinataires ont changé depuis la vérification (consentement, STOP ou route). Vérifiez de nouveau.',
  'SMS_CAMPAIGN_BLOCKED:MODULE_DISABLED': 'Envoi impossible : le module SMS est désactivé.',
  'SMS_CAMPAIGN_BLOCKED:SENDING_PAUSED': 'Envoi impossible : les nouveaux envois sont suspendus.',
  'SMS_CAMPAIGN_BLOCKED:NO_ADMISSIBLE': 'Envoi impossible : aucun numéro admissible.',
  'SMS_CAMPAIGN_BLOCKED:OVER_DAILY_CAP': 'Envoi impossible : le plafond quotidien serait dépassé.',
  SMS_CAMPAIGN_NOT_FOUND: 'Cet envoi est introuvable.',
}

export class SmsCommandError extends Error {
  constructor(public readonly code: string) {
    super(
      commandMessages[code] ??
        commandMessages[code.split(':')[0] ?? ''] ??
        'L’opération n’a pas pu être terminée. Réessayez dans un instant.',
    )
    this.name = 'SmsCommandError'
  }
}

/** Code métier (`raise exception 'CODE'`) extrait d'une erreur Supabase, sinon `UNEXPECTED`. */
export function smsErrorCode(message: string | undefined): string {
  const code = message?.trim() ?? ''
  return /^[A-Z][A-Z0-9_]+(:[A-Z0-9_]+)?$/.test(code) ? code : 'UNEXPECTED'
}

/** Phrase de résultat d'un SMS test : « en file » n'est pas « livré ». */
export function testResultMessage(result: SmsTestResult): string {
  if (result.state === 'BLOCKED') {
    return `SMS test refusé : ${reasonLabel(result.reason)}.`
  }
  if (result.replayed) return 'Une demande identique a déjà été faite cette minute : aucun second SMS.'
  return 'SMS test mis en file. Il part dans la minute ; son statut de livraison apparaîtra au journal.'
}
